import React, { useRef, useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { Player } from "lottie-react";
import bgImage1 from "../../assets/images/hero-image-19.jpg";
import bgImage2 from "../../assets/images/hero-image-1.jpg";
import { useNavigate } from "react-router-dom";
import LoadingOverlay from "../../shared/LoadingOverlay";
import InformationModal from "../../shared/InformationModal";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// 🔑 API BASE (local + production safe)
const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  "http://127.0.0.1:8000/api";


const Contact = () => {
  const formRef = useRef(null);
  const controls = useAnimation();
  const navigate = useNavigate();

  const [values, setValues] = useState({
    user_name: "",
    user_email: "",
    message: "Hello BroadBoy Adventures team,\n\n",
    city: "",
    country: "",
    phone: "",
    occupation: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [lottieData, setLottieData] = useState(null);

  useEffect(() => {
    fetch("/assets/lottie/success.json")
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((json) => setLottieData(json))
      .catch(() => setLottieData(null));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const validate = () => {
    const e = {};
    if (!values.user_name.trim()) e.user_name = "Please enter your name.";
    if (!values.user_email.trim()) e.user_email = "Please enter your email.";
    else if (!emailRegex.test(values.user_email))
      e.user_email = "Please enter a valid email.";
    if (!values.message.trim()) e.message = "Please enter your message.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    setValues((v) => ({ ...v, [e.target.name]: e.target.value }));
    setErrors((err) => ({ ...err, [e.target.name]: undefined }));
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    setToast(null);

    if (!validate()) {
      controls.start({
        x: [0, -8, 8, -6, 6, -3, 3, 0],
        transition: { duration: 0.6, ease: "easeInOut" },
      });
      setToast({ type: "error", text: "Please fix the errors." });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...values,
          occupation:
            values.occupation && values.occupation.trim()
              ? values.occupation.trim()
              : "Not specified",
        }),
      });

      const data = await response.json();
      setLoading(false);

      if (response.ok && data.success) {
        setValues({
          user_name: "",
          user_email: "",
          message: "",
          city: "",
          country: "",
          phone: "",
          occupation: "",
        });
        setErrors({});
        setShowModal(true);
      } else {
        throw new Error(data?.error || "Failed to send");
      }
    } catch (err) {
      console.error("Email Error:", err);
      setLoading(false);
      setToast({ type: "error", text: "Failed to send. Try again later." });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    navigate("/");
  };
const inputBase =
    "p-4 rounded-xl border bg-black/20 text-white placeholder-gray-300 focus:outline-none focus:border-white focus:bg-white/20 w-full";
  const isFormValid =
    values.user_name.trim() !== "" &&
    emailRegex.test(values.user_email) &&
    values.message.trim() !== "" &&
    !loading;

  return (
    <div className="relative w-full min-h-screen overflow-hidden">
      <motion.img
        src={bgImage1}
        alt="Background 1"
        className="absolute top-0 left-0 w-full h-full object-cover"
        initial={{ scale: 1.2, y: 0 }}
        animate={{ scale: 1.25, y: -20 }}
        transition={{
          duration: 5,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "easeInOut",
        }}
      />
      <motion.img
        src={bgImage2}
        alt="Background 2"
        className="absolute top-0 left-0 w-full h-full object-cover mix-blend-overlay"
        initial={{ scale: 1.3, y: 0 }}
        animate={{ scale: 1.35, y: 20 }}
        transition={{
          duration: 6,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "easeInOut",
        }}
      />

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen p-32 px-6 md:px-20 space-y-16 text-white">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl"
        >
          <h1 className="text-5xl md:text-7xl font-bold leading-tight drop-shadow-xl">
            Let's Connect
          </h1>
          <p className="mt-6 text-lg md:text-xl text-gray-100">
            Send us a message to start your next luxury mountain adventure. 14 Peaks Basecamp, trekking, and expeditions await.
          </p>
        </motion.div>

        <motion.form
          ref={formRef}
          onSubmit={sendEmail}
          initial={{ opacity: 1, y: 0 }}
          className="w-full max-w-3xl flex flex-col space-y-6 bg-black/10 border border-white/20 backdrop-blur-xl p-10 rounded-3xl shadow-xl"
        >
          <motion.div animate={controls} className="flex flex-col space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col">
                <input
                  name="user_name"
                  type="text"
                  placeholder="Name"
                  value={values.user_name}
                  onChange={handleChange}
                  className={`${inputBase} ${errors.user_name ? "border-red-400" : "border-white/30"}`}
                  required
                />
                {errors.user_name && (
                  <p className="text-red-300 text-sm mt-1">{errors.user_name}</p>
                )}
              </div>

              <div className="flex flex-col">
                <input
                  name="user_email"
                  type="email"
                  placeholder="Email"
                  value={values.user_email}
                  onChange={handleChange}
                  className={`${inputBase} ${errors.user_email ? "border-red-400" : "border-white/30"}`}
                  required
                />
                {errors.user_email && (
                  <p className="text-red-300 text-sm mt-1">{errors.user_email}</p>
                )}
              </div>

              <div className="flex flex-col">
                <input
                  name="phone"
                  type="text"
                  placeholder="Phone Number"
                  value={values.phone}
                  onChange={handleChange}
                  className={`${inputBase} border-white/30`}
                />
              </div>

              <div className="flex flex-col">
                <input
                  name="city"
                  type="text"
                  placeholder="City"
                  value={values.city}
                  onChange={handleChange}
                  className={`${inputBase} border-white/30`}
                />
              </div>

              <div className="flex flex-col">
                <input
                  name="country"
                  type="text"
                  placeholder="Country"
                  value={values.country}
                  onChange={handleChange}
                  className={`${inputBase} border-white/30`}
                />
              </div>

              <div className="flex flex-col">
                <input
                  name="occupation"
                  type="text"
                  placeholder="Occupation"
                  value={values.occupation}
                  onChange={handleChange}
                  className={`${inputBase} border-white/30`}
                />
              </div>
            </div>

            <div className="flex flex-col">
              <textarea
                name="message"
                placeholder="Message"
                rows={6}
                value={values.message}
                onChange={handleChange}
                className={`${inputBase} ${errors.message ? "border-red-400" : "border-white/30"}`}
                required
              />
              {errors.message && (
                <p className="text-red-300 text-sm mt-1">{errors.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={!isFormValid}
              className={`px-6 py-3 rounded-xl font-semibold transition flex items-center justify-center
                ${
                  isFormValid
                    ? "bg-gray-600/50 hover:bg-gray-100/50 cursor-pointer"
                    : "bg-gray-600/20 cursor-not-allowed opacity-50"
                }
              `}
            >
              {loading ? (
                <span className="flex items-center gap-3">
                  <svg
                    className="animate-spin h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeOpacity="0.25"
                    />
                    <path
                      d="M22 12a10 10 0 00-10-10"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeOpacity="0.9"
                    />
                  </svg>
                  Sending...
                </span>
              ) : (
                "Send a Message"
              )}
            </button>

          </motion.div>
        </motion.form>
      <LoadingOverlay show={loading} />
      {toast?.type === "error" && (
        <div className="fixed bottom-6 right-6 px-6 py-3 rounded-xl shadow-lg z-50 bg-red-600 text-white">
          {toast.text}
        </div>
      )}
      <InformationModal
        show={showModal}
        lottieData={lottieData}
        title="Message Sent Successfully!"
        onClose={closeModal}
      />

      </div>
    </div>
  );
};

export default Contact;
